/**
 * PolitiqueConfidentialite — Page publique.
 *
 * V23.15.75 — Rédaction institutionnelle inspirée des standards de l'industrie
 * (formulations larges, catégories agrégées, périmètre étendu). Conserve les
 * mentions minimales exigées par le RGPD/nLPD.
 */
import { Link } from "react-router-dom";
import { ArrowLeft, Mail } from "lucide-react";

const LAST_UPDATED = "11 juillet 2026";
const EDITEUR = "Constante Partners SA";
const EDITEUR_ADDRESS = "30 boulevard Helvétique · 1207 Genève · Suisse";
const CONTACT_EMAIL = "contact@noamworks.ch";

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-24 space-y-3 py-6 border-t border-white/5" data-testid={`policy-section-${id}`}>
      <h2 className="text-lg md:text-xl font-bold text-white">{title}</h2>
      <div className="text-[13.5px] text-zinc-300 leading-relaxed space-y-3">{children}</div>
    </section>
  );
}

export default function PolitiqueConfidentialite() {
  return (
    <div className="min-h-screen bg-[#0A0A0F] text-white" data-testid="page-politique-confidentialite">
      {/* Bandeau */}
      <div className="border-b border-white/5 py-4 px-6">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <Link to="/" className="text-zinc-400 hover:text-white inline-flex items-center gap-1 text-[13px]" data-testid="policy-back">
            <ArrowLeft className="w-3.5 h-3.5" /> Retour
          </Link>
          <div className="ml-auto text-[10px] uppercase tracking-[0.2em] text-[#EF9F27] font-semibold">Noam · Politique</div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">Version</div>
        <div className="text-[11px] text-zinc-500 mt-0.5">Dernière mise à jour : <span className="text-white">{LAST_UPDATED}</span></div>
        <h1 className="text-3xl md:text-4xl font-bold mt-6 leading-tight">Politique de confidentialité</h1>
        <p className="text-[14px] text-zinc-400 mt-4 leading-relaxed">
          La présente politique décrit la manière dont {EDITEUR} (ci-après « Noam », « nous » ou
          « nos ») recueille, utilise, partage et protège les informations liées à l&apos;utilisation
          de ses services, produits, sites, technologies et fonctionnalités associées
          (collectivement, les « Services »). En accédant à nos Services ou en les utilisant,
          vous acceptez les pratiques décrites dans le présent document.
        </p>

        <Section id="editeur" title="1. Informations relatives au responsable">
          <p>
            Les Services sont fournis par <b>{EDITEUR}</b>, société de droit suisse dont le siège
            social se situe {EDITEUR_ADDRESS}. Le responsable du traitement peut être contacté à
            tout moment à l&apos;adresse suivante :{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#EF9F27] hover:underline inline-flex items-center gap-1">
              <Mail className="w-3 h-3" /> {CONTACT_EMAIL}
            </a>.
          </p>
        </Section>

        <Section id="donnees" title="2. Catégories d'informations concernées">
          <p>
            Dans le cadre de la fourniture, du maintien, de la personnalisation, de la sécurisation
            et de l&apos;amélioration continue de nos Services, nous sommes susceptibles de traiter
            différentes catégories d&apos;informations, incluant sans s&apos;y limiter :
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li>Les informations que vous fournissez directement lors de la création ou de la gestion de votre compte, ou dans le cadre de vos interactions avec les Services.</li>
            <li>Les informations relatives à votre utilisation des Services, à leur configuration et à leur environnement d&apos;exécution.</li>
            <li>Les informations générées ou dérivées de votre activité sur les Services, y compris les contenus, préférences, paramètres et configurations associés.</li>
            <li>Les informations techniques nécessaires au bon fonctionnement, à la stabilité, à la performance et à la sécurité de la plateforme.</li>
            <li>Toute autre information dont le traitement est nécessaire à l&apos;exécution des Services, à la conformité légale, ou à l&apos;exercice des intérêts légitimes du responsable du traitement.</li>
          </ul>
        </Section>

        <Section id="finalites" title="3. Finalités des traitements">
          <p>
            Les informations mentionnées à l&apos;article précédent peuvent être traitées pour l&apos;une
            ou plusieurs des finalités suivantes, dont la liste n&apos;est pas limitative :
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li>Fourniture, exploitation, maintenance, personnalisation et amélioration des Services.</li>
            <li>Évaluation continue de la qualité, de la pertinence et de l&apos;efficacité des fonctionnalités proposées.</li>
            <li>Développement de nouvelles fonctionnalités et adaptation de l&apos;expérience utilisateur.</li>
            <li>Communications relatives aux Services, notifications essentielles et informations pertinentes.</li>
            <li>Prévention, détection et lutte contre les usages contraires aux Conditions d&apos;utilisation, aux lois applicables, à la sécurité de la plateforme ou de ses utilisateurs.</li>
            <li>Respect des obligations légales, réglementaires ou judiciaires auxquelles nous sommes soumis.</li>
            <li>Analyse et compréhension des tendances d&apos;usage aux fins d&apos;amélioration des Services.</li>
            <li>Exercice ou défense de nos droits, intérêts et prérogatives.</li>
          </ul>
        </Section>

        <Section id="base-legale" title="4. Fondements juridiques">
          <p>
            Le traitement des informations vous concernant repose, selon le contexte et le
            traitement considéré, sur l&apos;un ou plusieurs des fondements juridiques suivants :
            l&apos;exécution du contrat qui nous lie ou de mesures précontractuelles prises à votre
            demande, votre consentement lorsque celui-ci est requis, le respect de nos obligations
            légales et réglementaires, notre intérêt légitime à faire fonctionner, sécuriser,
            améliorer et développer nos Services, ainsi que la sauvegarde d&apos;intérêts vitaux ou
            l&apos;exécution d&apos;une mission d&apos;intérêt public le cas échéant.
          </p>
        </Section>

        <Section id="partage" title="5. Partage d'informations">
          <p>
            Dans le cadre de la fourniture des Services et des finalités décrites ci-dessus, nous
            pouvons être amenés à partager tout ou partie des informations vous concernant avec :
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li>Les entités affiliées, filiales, sociétés du groupe ou partenaires commerciaux avec lesquels nous entretenons une relation contractuelle.</li>
            <li>Les prestataires techniques et sous-traitants agissant pour notre compte, sur instructions documentées et sous obligations contractuelles appropriées.</li>
            <li>Les autorités administratives, judiciaires ou réglementaires compétentes, lorsque cela est requis par la loi applicable ou nécessaire à la protection de nos droits, de nos utilisateurs ou de tiers.</li>
            <li>Toute personne dûment autorisée dans le cadre d&apos;une opération de restructuration, cession, fusion ou opération assimilée affectant tout ou partie des Services.</li>
          </ul>
          <p className="pt-2 text-[12.5px] text-zinc-400">
            Nous ne procédons à aucune vente de vos informations personnelles à des tiers à des
            fins de démarchage commercial.
          </p>
        </Section>

        <Section id="stockage" title="6. Hébergement et transferts internationaux">
          <p>
            Nos Services s&apos;appuient sur une infrastructure cloud globale. Vos informations sont
            susceptibles d&apos;être hébergées, traitées ou consultées depuis différentes juridictions,
            y compris en dehors de la Suisse et de l&apos;Espace économique européen.
          </p>
          <p>
            Lorsque de tels transferts ont lieu, ils sont encadrés par les mécanismes juridiques
            appropriés, incluant, selon les cas, les décisions d&apos;adéquation adoptées par la
            Commission européenne, les Clauses Contractuelles Types, les règles d&apos;entreprise
            contraignantes, ou toute autre garantie prévue par la réglementation applicable.
          </p>
        </Section>

        <Section id="conservation" title="7. Durée de conservation">
          <p>
            Les informations vous concernant sont conservées aussi longtemps que nécessaire pour
            atteindre les finalités décrites dans la présente politique, ainsi que pour se
            conformer à nos obligations légales, résoudre les litiges éventuels et faire respecter
            nos accords. Les durées applicables varient selon la nature des informations
            considérées, le contexte du traitement et les exigences légales applicables.
          </p>
        </Section>

        <Section id="droits" title="8. Vos droits">
          <p>
            Sous réserve des conditions et limites fixées par la réglementation applicable
            (notamment le Règlement général sur la protection des données et la loi fédérale
            suisse sur la protection des données), vous disposez des droits suivants sur les
            informations vous concernant :
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li>Droit d&apos;accès aux données.</li>
            <li>Droit de rectification.</li>
            <li>Droit à l&apos;effacement.</li>
            <li>Droit à la limitation du traitement.</li>
            <li>Droit à la portabilité.</li>
            <li>Droit d&apos;opposition.</li>
            <li>Droit de retirer votre consentement, lorsque le traitement est fondé sur celui-ci.</li>
          </ul>
          <p className="pt-2">
            Ces droits peuvent être exercés en nous adressant une demande à{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#EF9F27] hover:underline">{CONTACT_EMAIL}</a>.
            Nous pouvons être amenés à vous demander de justifier votre identité afin de traiter
            votre demande dans les délais légaux. Vous disposez également du droit d&apos;introduire une
            réclamation auprès de l&apos;autorité de contrôle compétente.
          </p>
        </Section>

        <Section id="securite" title="9. Sécurité">
          <p>
            Nous mettons en œuvre des mesures techniques et organisationnelles conformes aux
            standards du secteur pour garantir un niveau de sécurité adapté aux risques. Aucune
            méthode de transmission ou de stockage électronique n&apos;étant totalement inviolable,
            nous ne pouvons garantir une sécurité absolue.
          </p>
        </Section>

        <Section id="cookies" title="10. Cookies et technologies similaires">
          <p>
            Les Services utilisent des cookies et technologies analogues nécessaires à leur
            fonctionnement, à leur sécurisation, à la mémorisation de vos préférences et, le cas
            échéant, à des fins d&apos;analyse et d&apos;amélioration. Le paramétrage de votre navigateur
            vous permet, dans une certaine mesure, d&apos;accepter, refuser ou supprimer ces cookies.
          </p>
        </Section>

        <Section id="mineurs" title="11. Utilisation par des mineurs">
          <p>
            Les Services ne sont pas destinés aux personnes n&apos;ayant pas atteint l&apos;âge légal
            requis dans leur juridiction pour consentir seules au traitement de leurs données
            personnelles. Nous ne collectons pas sciemment d&apos;informations concernant les mineurs.
          </p>
        </Section>

        <Section id="modifications" title="12. Modifications">
          <p>
            La présente politique peut être modifiée à tout moment afin de refléter l&apos;évolution
            de nos Services, du cadre légal applicable ou de nos pratiques. Les modifications
            prennent effet dès leur publication. Il vous appartient de consulter régulièrement
            cette page pour prendre connaissance de la version en vigueur.
          </p>
        </Section>

        <Section id="contact" title="13. Contact">
          <p>
            Pour toute question, demande ou information relative à la présente politique, vous
            pouvez nous écrire à{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#EF9F27] hover:underline">{CONTACT_EMAIL}</a>{" "}
            ou nous adresser un courrier postal à l&apos;attention de {EDITEUR}, {EDITEUR_ADDRESS}.
          </p>
        </Section>

        <div className="mt-10 pt-6 border-t border-white/5 text-center text-[11px] text-zinc-500">
          © {new Date().getFullYear()} {EDITEUR} · Politique de confidentialité — version du {LAST_UPDATED}
        </div>
      </div>
    </div>
  );
}
